import {
    createContext,
    useContext,
    useMemo,
    useState,
    useCallback,
    useRef,
    useEffect,
} from "react";
import { createSeededNoise } from "../../utils/perlinNoise";
import { useToolParamPersistence } from "../../utils/toolParamPersistence";

const DEFAULT_RAMP = " .'`^\",:;Il!i><~+_-?][}{1)(|\\/tfjrxnuvczXYUJCLQ0OZmwqpdbkhao*#MW&8%B@$";

export interface AsciiChar {
    x: number;
    y: number;
    char: string;
}

interface AsciiArtState {
    imageDataUrl: string | null;
    imageName: string | null;
    cols: number;
    rows: number;
    cellWidth: number;
    cellHeight: number;
    font: string;
    fontSize: number;
    noiseAmount: number;
    noiseScale: number;
    seed: string;
    invert: boolean;
    characterRamp: string;
    lockAspectRatio: boolean;
    imageAspectRatio: number | null;
    asciiGrid: AsciiChar[];
    viewBoxAttr: string;
    canExport: boolean;

    setCols: (cols: number) => void;
    setRows: (rows: number) => void;
    setCellWidth: (w: number) => void;
    setCellHeight: (h: number) => void;
    setFont: (font: string) => void;
    setFontSize: (size: number) => void;
    setNoiseAmount: (amount: number) => void;
    setNoiseScale: (scale: number) => void;
    setSeed: (seed: string) => void;
    setInvert: (invert: boolean) => void;
    setCharacterRamp: (ramp: string) => void;
    setLockAspectRatio: (lock: boolean) => void;
    handleImageLoad: (event: React.ChangeEvent<HTMLInputElement>) => void;
    handleJsonImport: (event: React.ChangeEvent<HTMLInputElement>) => void;
    buildExportSvgString: () => string;
    handleDownload: () => void;
    imageInputRef: React.RefObject<HTMLInputElement>;
    jsonInputRef: React.RefObject<HTMLInputElement>;
    applyPreset: (values: Record<string, unknown>) => void;
    getPresetValues: () => Record<string, unknown>;
    resetParameters: () => void;
}

const AsciiArtContext = createContext<AsciiArtState | null>(null);

export function useAsciiArt() {
    const context = useContext(AsciiArtContext);
    if (!context) {
        throw new Error("useAsciiArt must be used within AsciiArtProvider");
    }
    return context;
}

interface AsciiArtProviderProps {
    children: React.ReactNode;
}

export function AsciiArtProvider({ children }: AsciiArtProviderProps) {
    const [imageDataUrl, setImageDataUrl] = useState<string | null>(null);
    const [imageName, setImageName] = useState<string | null>(null);
    const [cols, setCols] = useState(80);
    const [rows, setRows] = useState(60);
    const [cellWidth, setCellWidth] = useState(10);
    const [cellHeight, setCellHeight] = useState(16);
    const [font, setFont] = useState("monospace");
    const [fontSize, setFontSize] = useState(14);
    const [noiseAmount, setNoiseAmount] = useState(0);
    const [noiseScale, setNoiseScale] = useState(0.1);
    const [seed, setSeed] = useState("");
    const [invert, setInvert] = useState(false);
    const [characterRamp, setCharacterRamp] = useState(DEFAULT_RAMP);
    const [lockAspectRatio, setLockAspectRatio] = useState(false);
    const [imageAspectRatio, setImageAspectRatio] = useState<number | null>(null);
    const [brightnessData, setBrightnessData] = useState<number[] | null>(null);

    const imageInputRef = useRef<HTMLInputElement | null>(null);
    const jsonInputRef = useRef<HTMLInputElement | null>(null);

    const sampleBrightness = useCallback(
        (dataUrl: string, targetCols: number, targetRows: number) => {
            const img = new Image();
            img.onload = () => {
                const canvas = document.createElement("canvas");
                canvas.width = targetCols;
                canvas.height = targetRows;
                const ctx = canvas.getContext("2d");
                if (!ctx) return;

                ctx.imageSmoothingEnabled = true;
                ctx.drawImage(img, 0, 0, targetCols, targetRows);
                const imageData = ctx.getImageData(
                    0,
                    0,
                    targetCols,
                    targetRows
                );
                const data = imageData.data;

                const brightness: number[] = [];
                for (let i = 0; i < data.length; i += 4) {
                    const r = data[i];
                    const g = data[i + 1];
                    const b = data[i + 2];
                    brightness.push(
                        (0.299 * r + 0.587 * g + 0.114 * b) / 255
                    );
                }
                setBrightnessData(brightness);
            };
            img.src = dataUrl;
        },
        []
    );

    const handleImageLoad = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            const file = event.target.files?.[0];
            if (!file) return;

            if (!file.type.startsWith("image/")) return;

            const reader = new FileReader();
            reader.onload = () => {
                const dataUrl =
                    typeof reader.result === "string" ? reader.result : "";
                if (!dataUrl) return;

                const img = new Image();
                img.onload = () => {
                    const ratio = img.naturalWidth / img.naturalHeight;
                    setImageAspectRatio(ratio);
                    setImageDataUrl(dataUrl);
                    setImageName(file.name);

                    if (lockAspectRatio) {
                        const newCols = Math.max(1, Math.round(rows * ratio * (cellHeight / cellWidth)));
                        setCols(newCols);
                        sampleBrightness(dataUrl, newCols, rows);
                    } else {
                        sampleBrightness(dataUrl, cols, rows);
                    }
                };
                img.src = dataUrl;
            };
            reader.readAsDataURL(file);
        },
        [cols, rows, cellWidth, cellHeight, lockAspectRatio, sampleBrightness]
    );

    const handleJsonImport = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            const file = event.target.files?.[0];
            if (!file) return;

            const reader = new FileReader();
            reader.onload = () => {
                try {
                    const text =
                        typeof reader.result === "string" ? reader.result : "";
                    const json = JSON.parse(text);
                    if (
                        typeof json.characters === "string" &&
                        json.characters.length > 0
                    ) {
                        setCharacterRamp(json.characters);
                    }
                } catch {
                    // ignore malformed JSON
                }
            };
            reader.readAsText(file);

            if (jsonInputRef.current) {
                jsonInputRef.current.value = "";
            }
        },
        []
    );

    // Auto-compute cols from rows when aspect ratio is locked
    useEffect(() => {
        if (lockAspectRatio && imageAspectRatio !== null) {
            const newCols = Math.max(1, Math.round(rows * imageAspectRatio * (cellHeight / cellWidth)));
            setCols(newCols);
        }
    }, [lockAspectRatio, imageAspectRatio, rows, cellWidth, cellHeight]);

    // Re-sample brightness when cols/rows change and image is loaded
    useMemo(() => {
        if (imageDataUrl) {
            sampleBrightness(imageDataUrl, cols, rows);
        }
    }, [imageDataUrl, cols, rows, sampleBrightness]);

    const asciiGrid = useMemo(() => {
        if (!brightnessData || brightnessData.length !== cols * rows) return [];

        const ramp = characterRamp || DEFAULT_RAMP;
        const rampLen = ramp.length;
        if (rampLen === 0) return [];

        const seededNoise = createSeededNoise(seed || "ascii-default");
        const chars: AsciiChar[] = [];

        for (let row = 0; row < rows; row++) {
            for (let col = 0; col < cols; col++) {
                const idx = row * cols + col;
                let brightness = brightnessData[idx];

                if (noiseAmount > 0) {
                    const noise =
                        (seededNoise(col * noiseScale, row * noiseScale) -
                            0.5) *
                        2 *
                        noiseAmount;
                    brightness = Math.max(0, Math.min(1, brightness + noise));
                }

                if (invert) {
                    brightness = 1 - brightness;
                }

                const charIndex = Math.min(
                    Math.floor(brightness * rampLen),
                    rampLen - 1
                );

                chars.push({
                    x: col * cellWidth,
                    y: row * cellHeight + fontSize,
                    char: ramp[charIndex],
                });
            }
        }

        return chars;
    }, [
        brightnessData,
        cols,
        rows,
        cellWidth,
        cellHeight,
        fontSize,
        characterRamp,
        noiseAmount,
        noiseScale,
        seed,
        invert,
    ]);

    const viewBoxAttr = `0 0 ${cols * cellWidth} ${rows * cellHeight}`;
    const canExport = asciiGrid.length > 0;

    const buildExportSvgString = useCallback(() => {
        if (!asciiGrid.length) return "";

        const escapeXml = (s: string) =>
            s
                .replace(/&/g, "&amp;")
                .replace(/</g, "&lt;")
                .replace(/>/g, "&gt;")
                .replace(/"/g, "&quot;");

        const textElements = asciiGrid
            .map(
                (c) =>
                    `    <text x="${c.x}" y="${c.y}" font-family="${font}" font-size="${fontSize}" fill="black">${escapeXml(c.char)}</text>`
            )
            .join("\n");

        return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${viewBoxAttr}">
  <g class="ascii-art">
${textElements}
  </g>
</svg>`;
    }, [asciiGrid, font, fontSize, viewBoxAttr]);

    const handleDownload = useCallback(() => {
        if (!canExport) return;
        const svgString = buildExportSvgString();
        if (!svgString) return;

        const blob = new Blob([svgString], {
            type: "image/svg+xml;charset=utf-8",
        });
        const url = URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = url;
        const baseName = imageName
            ? imageName.replace(/\.[^.]+$/, "")
            : "ascii-art";
        link.download = `${baseName}-ascii-${cols}x${rows}.svg`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    }, [canExport, buildExportSvgString, imageName, cols, rows]);

    const getPresetValues = useCallback(
        (): Record<string, unknown> => ({
            cols, rows, cellWidth, cellHeight,
            font, fontSize, noiseAmount, noiseScale, seed,
            invert, characterRamp, lockAspectRatio,
        }),
        [
            cols, rows, cellWidth, cellHeight,
            font, fontSize, noiseAmount, noiseScale, seed,
            invert, characterRamp, lockAspectRatio,
        ],
    );

    const applyPreset = useCallback((values: Record<string, unknown>) => {
        if (values.cols != null) setCols(values.cols as number);
        if (values.rows != null) setRows(values.rows as number);
        if (values.cellWidth != null) setCellWidth(values.cellWidth as number);
        if (values.cellHeight != null) setCellHeight(values.cellHeight as number);
        if (values.font != null) setFont(values.font as string);
        if (values.fontSize != null) setFontSize(values.fontSize as number);
        if (values.noiseAmount != null) setNoiseAmount(values.noiseAmount as number);
        if (values.noiseScale != null) setNoiseScale(values.noiseScale as number);
        if (values.seed != null) setSeed(values.seed as string);
        if (values.invert != null) setInvert(values.invert as boolean);
        if (values.characterRamp != null) setCharacterRamp(values.characterRamp as string);
        if (values.lockAspectRatio != null) setLockAspectRatio(values.lockAspectRatio as boolean);
    }, []);

    const { resetParameters } = useToolParamPersistence({
        toolId: "ascii-art",
        defaultPreset: {
            cols: 80,
            rows: 60,
            cellWidth: 10,
            cellHeight: 16,
            font: "monospace",
            fontSize: 14,
            noiseAmount: 0,
            noiseScale: 0.1,
            seed: "",
            invert: false,
            characterRamp: DEFAULT_RAMP,
            lockAspectRatio: false,
        },
        getPresetValues,
        applyPreset,
    });

    const value: AsciiArtState = {
        imageDataUrl,
        imageName,
        cols,
        rows,
        cellWidth,
        cellHeight,
        font,
        fontSize,
        noiseAmount,
        noiseScale,
        seed,
        invert,
        characterRamp,
        lockAspectRatio,
        imageAspectRatio,
        asciiGrid,
        viewBoxAttr,
        canExport,
        setCols,
        setRows,
        setCellWidth,
        setCellHeight,
        setFont,
        setFontSize,
        setNoiseAmount,
        setNoiseScale,
        setSeed,
        setInvert,
        setCharacterRamp,
        setLockAspectRatio,
        handleImageLoad,
        handleJsonImport,
        buildExportSvgString,
        handleDownload,
        imageInputRef,
        jsonInputRef,
        applyPreset,
        getPresetValues,
        resetParameters,
    };

    return (
        <AsciiArtContext.Provider value={value}>
            {children}
        </AsciiArtContext.Provider>
    );
}
